import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ===== Styled Components =====
const Wrapper = styled.div`
  position: relative;
  padding: 1rem;
  background: #f9f9f9;

  /* Fill the parent card */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;

  /* keep filters small so the chart gets space */
  min-height: 36px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  z-index: 10;
  &:hover { color: #000; }
  svg { width: 20px; height: 20px; }
`;

/* Chart container that expands */
const ChartBox = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
  padding: 12px;

  /* Take all remaining height in the card */
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

/* Wrapper that gives the canvas explicit height */
const CanvasWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const dayNamesPL = [
  "Niedziela","Poniedziałek","Wtorek","Środa","Czwartek","Piątek","Sobota"
];

const monthNamesPL = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

const AveragePurchasesByWeekday = ({ data }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartData, setChartData] = useState(null);

  const calculatePurchases = (row) => parseFloat(row.K_42) || 0;

  // Years
  useEffect(() => {
    const years = Array.from(
      new Set((data || [])
        .map((row) => parseInt(row.Rok, 10))
        .filter((y) => !isNaN(y)))
    ).sort((a, b) => a - b);

    setAvailableYears(years);
    if (!selectedYear && years.length > 0) {
      setSelectedYear(years[years.length - 1]);
    }
  }, [data]);

  // Months for selected year
  useEffect(() => {
    if (!selectedYear) return;

    const months = Array.from(
      new Set((data || [])
        .filter((row) => parseInt(row.Rok, 10) === selectedYear)
        .map((row) => parseInt(row.Miesiac, 10))
        .filter((m) => !isNaN(m)))
    ).sort((a, b) => a - b);

    setAvailableMonths(months);
    if (!selectedMonth && months.length > 0) {
      setSelectedMonth(months[0]);
    }
  }, [selectedYear, data]);

  // Chart data
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    const totals = Array(7).fill(0);
    const counts = Array(7).fill(0);
    const totalsPrev = Array(7).fill(0);
    const countsPrev = Array(7).fill(0);

    (data || []).forEach((row) => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      if (month !== selectedMonth) return;

      const date = new Date(row.DataZakupu);
      if (isNaN(date)) return;
      const weekday = date.getDay();
      const value = calculatePurchases(row);

      if (year === selectedYear) {
        totals[weekday] += value;
        counts[weekday] += 1;
      } else if (year === selectedYear - 1) {
        totalsPrev[weekday] += value;
        countsPrev[weekday] += 1;
      }
    });

    const averages = totals.map((sum, idx) => (counts[idx] > 0 ? sum / counts[idx] : 0));
    const averagesPrev = totalsPrev.map((sum, idx) => (countsPrev[idx] > 0 ? sum / countsPrev[idx] : null));

    setChartData({
      labels: dayNamesPL,
      datasets: [
        {
          label: `Średnia ${selectedYear}`,
          data: averages,
          backgroundColor: "#9848c7",          // current: purple
          borderColor: "#9848c7",
          borderWidth: 1,
        },
        {
          label: `Średnia ${selectedYear - 1}`,
          data: averagesPrev,
          backgroundColor: "rgba(220, 53, 69, 0.6)", // previous: red (semi)
          borderColor: "rgba(220, 53, 69, 1)",
          borderWidth: 1,
        },
      ],
    });
  }, [selectedYear, selectedMonth, data]);

  return (
    <Wrapper>
      <InfoIconWrapper data-tooltip-id="average-purchases-weekday-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="average-purchases-weekday-tooltip"
        place="left"
        content={
          <>
            <strong>Średnie zakupy dzienne</strong><br />
            Zakupy uśrednione wg dnia tygodnia,<br />
            porównanie miesiąca {selectedMonth ? monthNamesPL[selectedMonth - 1] : ""} dla {selectedYear} i {selectedYear ? selectedYear - 1 : ""}.
          </>
        }
      />

      <FiltersWrapper>
        <label>
          Rok:&nbsp;
          <select
            value={selectedYear || ""}
            onChange={(e) => {
              setSelectedYear(parseInt(e.target.value, 10));
              setSelectedMonth(null);
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </label>

        <label>
          Miesiąc:&nbsp;
          <select
            value={selectedMonth || ""}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
          >
            {availableMonths.map((month) => (
              <option key={month} value={month}>{monthNamesPL[month - 1]}</option>
            ))}
          </select>
        </label>
      </FiltersWrapper>

      <ChartBox>
        <CanvasWrap>
          {chartData && (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,   // critical for filling height
                plugins: {
                  title: {
                    display: true,
                    text: `Porównanie średniej dla dnia tygodnia (${selectedMonth ? monthNamesPL[selectedMonth - 1] : ""} ${selectedYear || ""})`,
                  },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `Średnia: ${ctx.raw?.toLocaleString("pl-PL", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                    },
                  },
                  legend: { position: "top" },
                },
                scales: {
                  x: {
                    grid: { display: false },
                  },
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: "Zakupy (PLN)",
                    },
                    ticks: {
                      callback: (value) =>
                        Number(value)
                          .toFixed(0)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, " "),
                    },
                  },
                },
              }}
            />
          )}
        </CanvasWrap>
      </ChartBox>
    </Wrapper>
  );
};

export default AveragePurchasesByWeekday;
